(() => {
  const COLLAPSED_HEIGHT = 250;

  const getParts = (root) => {
    const wrapper = root.closest?.('.js-product-description') || root;
    if (!wrapper || !wrapper.classList.contains('js-product-description')) return null;
    const content = wrapper.querySelector('.product-description__content');
    const button = wrapper.querySelector('.product-description__toggle');
    if (!content || !button) return null;
    return { wrapper, content, button };
  };

  const collapse = (parts) => {
    const height = Number(parts.wrapper.dataset.collapsedHeight) || COLLAPSED_HEIGHT;
    parts.wrapper.classList.remove('expanded', 'is-expanded');
    parts.content.style.maxHeight = `${height}px`;
    parts.content.style.overflow = 'hidden';
    parts.button.textContent = 'Read more';
    parts.button.setAttribute('aria-expanded', 'false');
  };

  const expand = (parts) => {
    parts.wrapper.classList.add('expanded', 'is-expanded');
    parts.content.style.maxHeight = 'none';
    parts.content.style.overflow = 'visible';
    parts.button.textContent = 'Read less';
    parts.button.setAttribute('aria-expanded', 'true');
  };

  const syncButtonVisibility = (wrapper) => {
    const parts = getParts(wrapper);
    if (!parts) return;
    const height = Number(parts.wrapper.dataset.collapsedHeight) || COLLAPSED_HEIGHT;
    const needsToggle = parts.content.scrollHeight > height + 10;
    parts.button.style.display = needsToggle ? '' : 'none';
    if (!needsToggle) {
      expand(parts);
      parts.button.style.display = 'none';
      return;
    }
    if (!parts.wrapper.classList.contains('is-expanded') && !parts.wrapper.classList.contains('expanded')) {
      collapse(parts);
    }
  };

  const syncAll = () => {
    document.querySelectorAll('.js-product-description').forEach(syncButtonVisibility);
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('.product-description__toggle');
      if (!button) return;

      const parts = getParts(button);
      if (!parts) return;

      event.preventDefault();
      event.stopPropagation();

      if (parts.wrapper.classList.contains('is-expanded') || parts.wrapper.classList.contains('expanded')) {
        collapse(parts);
      } else {
        expand(parts);
      }
    },
    true
  );

  // Tabs (existing)
  document.addEventListener('DOMContentLoaded', () => {
    const tabButtons = document.querySelectorAll('.nativedsd-product-details__btn');
    const tabPanels = document.querySelectorAll('.nativedsd-product-details__panel');

    tabButtons.forEach((tab) => {
      tab.addEventListener('click', () => {
        tabButtons.forEach((btn) => btn.classList.remove('active'));
        tabPanels.forEach((panel) => panel.classList.remove('active'));

        tab.classList.add('active');
        document.getElementById(tab.dataset.tab)?.classList.add('active');
      });
    });

    // Audio player: the tracklist plays through the shared audio engine (assets/nativedsd-audio.js), the same one the
    // mini-player at the bottom of the page belongs to. Playing a track here makes the tracklist the engine's queue:
    // the mini-player shows that track and its previous / next buttons walk through this album's previews. A track
    // that was started somewhere else (a cover on a browse page) is replaced.
    const list = document.querySelector('.nativedsd-tracklist');
    const trackButtons = Array.from(document.querySelectorAll('.nativedsd-track-play'));

    const setPlaying = (button, playing) => {
      button.classList.toggle('is-playing', playing);
      button.closest('.nativedsd-track-item')?.classList.toggle('is-playing', playing);
    };

    const pageTracks = () =>
      trackButtons.map((button) => ({
        src: button.dataset.audio,
        title:
          button.dataset.title ||
          button.closest('.nativedsd-track-item')?.querySelector('.nativedsd-track-item__title')?.textContent.replace(/\s+/g, ' ').trim() ||
          '',
        subtitle: list?.dataset.albumSubtitle || '',
        artwork: list?.dataset.albumArtwork || '',
        url: list?.dataset.albumUrl || location.pathname,
      }));

    // rows follow the engine: the playing one shows pause, the others play
    const syncTracklist = () => {
      const engine = window.NativeDSDAudio;
      if (!engine) return;
      const paused = engine.state().paused;
      trackButtons.forEach((button) => setPlaying(button, engine.isCurrent(button.dataset.audio) && !paused));
    };
    ['track', 'play', 'pause', 'ended', 'stop', 'error'].forEach((name) =>
      document.addEventListener(`ndsd-audio:${name}`, syncTracklist)
    );

    trackButtons.forEach((button, i) => {
      button.addEventListener('click', () => {
        const engine = window.NativeDSDAudio;
        if (!engine) return;
        if (engine.isCurrent(button.dataset.audio)) {
          engine.toggle();
          return;
        }
        // the whole tracklist is the queue from this track on: the player carries on with the next track by itself
        engine.playQueue(pageTracks(), i);
      });
    });
    syncTracklist();

    syncAll();
  });

  window.addEventListener('load', syncAll);

  // Section Rendering API / variant changes replace product HTML.
  document.addEventListener('shopify:section:load', syncAll);
  document.addEventListener('product-info:loaded', syncAll);
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches?.('.js-product-description') || node.querySelector?.('.js-product-description')) {
          syncAll();
          return;
        }
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();

/* Play button on the album cover: presses the first play button of the tracklist (or, while a track plays, pauses
   that one), and follows the tracklist's state (pause icon while any track plays). */
(() => {
  const trackButtons = () => Array.from(document.querySelectorAll('.nativedsd-track-play'));

  document.addEventListener('click', (event) => {
    const cover = event.target.closest('[data-ndsd-cover-play]');
    if (!cover) return;
    event.preventDefault();
    event.stopPropagation();
    const buttons = trackButtons();
    const target = buttons.find((b) => b.classList.contains('is-playing')) || buttons[0];
    if (target) target.click();
  });

  const sync = () => {
    const playing = trackButtons().some((b) => b.classList.contains('is-playing'));
    document.querySelectorAll('[data-ndsd-cover-play]').forEach((cover) => {
      cover.classList.toggle('is-playing', playing);
      cover.setAttribute('aria-pressed', playing ? 'true' : 'false');
    });
  };

  // the tracklist toggles .is-playing on its buttons: mirror it on the cover button
  new MutationObserver(sync).observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });
})();

/* Tracklist: a SKU in a track title, "(from FR724SACD)", becomes a link to that product. Product handles start with the
   lowercased SKU, so the SKU is looked up with the storefront suggest endpoint and the link is only added once a
   product with a matching handle is found. Lookups are cached for the session. */
(() => {
  const titles = Array.from(document.querySelectorAll('.nativedsd-track-item__title'));
  if (!titles.length) return;

  const FROM = /\(from\s+([A-Za-z0-9._-]+)\)/;
  const ownHandle = (window.location.pathname.match(/\/products\/([^/?]+)/) || [])[1] || '';
  const cache = new Map();

  const lookup = (sku) => {
    const key = sku.toLowerCase();
    if (cache.has(key)) return cache.get(key);
    let stored = null;
    try { stored = sessionStorage.getItem(`ndsd-sku:${key}`); } catch (e) {}
    const promise =
      stored !== null
        ? Promise.resolve(stored || null)
        : fetch(
            `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(sku)}` +
              '&resources[type]=product&resources[limit]=10&resources[options][fields]=variants.sku,title'
          )
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
              const products = data?.resources?.results?.products || [];
              const matches = products.filter((p) => p.handle === key || p.handle.startsWith(`${key}-`));
              // a download and a vinyl can share a SKU prefix: the download first
              const best = matches.find((p) => !p.handle.startsWith(`${key}-vinyl`)) || matches[0];
              const url = best ? best.url.split('?')[0] : '';
              try { sessionStorage.setItem(`ndsd-sku:${key}`, url); } catch (e) {}
              return url || null;
            })
            .catch(() => null);
    cache.set(key, promise);
    return promise;
  };

  titles.forEach((title) => {
    const node = Array.from(title.childNodes).find((n) => n.nodeType === 3 && FROM.test(n.textContent));
    if (!node) return;
    const sku = node.textContent.match(FROM)[1];
    if (ownHandle.startsWith(`${sku.toLowerCase()}-`)) return;

    lookup(sku).then((url) => {
      if (!url || !node.parentNode) return;
      const text = node.textContent;
      const at = text.indexOf(sku, text.search(FROM));
      const link = document.createElement('a');
      link.href = url;
      link.textContent = sku;
      link.className = 'nativedsd-track-item__sku';
      const after = node.splitText(at);
      after.textContent = after.textContent.slice(sku.length);
      node.parentNode.insertBefore(link, after);
    });
  });
})();
